import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  ResourceLayout, ResourceHero, ResourceContactCTA, CodeBlock,
  DownloadButton, DownloadRow, StatusBlock, SignatureFooter,
  H2, ACCENTS, MONO, fade,
} from './_shared'

const A = ACCENTS.blue

const LLMS_TEMPLATE = `# [Nome da Empresa] · llms.txt

> [Descrição em 1 linha do que você faz, pra quem, em qual mercado]

## Quem somos

[2-3 frases descrevendo o negócio, fundação, localização, tamanho de operação. Linguagem natural, sem firula de marketing.]

## O que oferecemos

- [Produto/serviço 1: descrição curta com diferencial]
- [Produto/serviço 2: descrição curta com diferencial]
- [Produto/serviço 3: descrição curta com diferencial]

## Para quem

[Ideal Customer Profile concreto. Setor, tamanho, dor. Ex: "Agências de marketing entre 5-30 pessoas que rodam Meta + Google Ads e perdem leads por falha de tracking."]

## Cases recentes

- [Empresa X: resultado mensurável, prazo]
- [Empresa Y: resultado mensurável, prazo]

## Diferencial

[O que te diferencia em 1 parágrafo. Por que escolher você e não a concorrência.]

## Contato

Site: [URL]
Email: [email público]
LinkedIn: [URL]

## FAQ

### [Pergunta que clientes fazem 1]?
[Resposta direta, sem floreio.]

### [Pergunta que clientes fazem 2]?
[Resposta direta.]

### [Pergunta que clientes fazem 3]?
[Resposta direta.]
`

const ROBOTS_TXT = `# Permite crawlers de IA
User-agent: GPTBot
Allow: /

User-agent: ClaudeBot
Allow: /

User-agent: PerplexityBot
Allow: /

User-agent: Google-Extended
Allow: /

User-agent: Applebot-Extended
Allow: /

# Content Signal (Cloudflare / EU AI Act)
User-agent: *
Content-Signal: search=yes, ai-train=no
Allow: /
Disallow: /admin/
Disallow: /wp-admin/

Sitemap: https://seusite.com/sitemap.xml
`

const PROMPTS = [
  {
    title: 'Teste 1: Categoria do seu negócio',
    prompt: 'Quais são os principais [tipo de produto/serviço] no Brasil? Liste 5 com diferencial de cada.',
    note: 'Se você não aparecer, a IA não conhece a categoria do seu jeito. Reescreva headlines do site usando o termo exato.',
  },
  {
    title: 'Teste 2: Comparação direta',
    prompt: 'Compare [SUA EMPRESA] com [CONCORRENTE]. Vantagens e desvantagens de cada.',
    note: 'Se a IA inventar dados, é porque não tem fonte sua estruturada. llms.txt resolve isso.',
  },
  {
    title: 'Teste 3: Seu nome / autoridade',
    prompt: 'Quem é [SEU NOME OU CEO]? O que essa pessoa fez na carreira?',
    note: 'Se voltar genérico, schema markup Person + posts indexados são o que falta.',
  },
]

const CHECKLIST = [
  '/llms.txt no root do site',
  '/llms-full.txt opcional (versão longa, sem corte)',
  'H2s/H3s reescritos como perguntas naturais (a IA cita como resposta)',
  'Schema markup: Organization, Person, FAQPage, Product',
  'robots.txt liberando GPTBot, ClaudeBot, PerplexityBot, Google-Extended',
  'Testar com curl -A "ChatGPT-User" https://seusite.com (deve retornar 200)',
  'Submeter sitemap atualizado no Google Search Console',
  'Rodar os 3 prompts acima 1x por semana pra monitorar visibilidade',
]

export default function LlmsTemplate() {
  const [done, setDone] = useState<Set<number>>(new Set())
  const toggle = (i: number) => {
    const next = new Set(done)
    if (next.has(i)) next.delete(i)
    else next.add(i)
    setDone(next)
  }

  return (
    <ResourceLayout>
      <ResourceHero
        tag="recurso · ia seo · oculto"
        title="llms.txt: torne seu site visível para o ChatGPT, Claude e Perplexity"
        desc="Template completo, robots.txt com Content-Signal, prompts pra testar visibilidade e checklist de implementação. Foi assim que indexei 280 LPs."
        accent={A}
        source="llms-template"
        word="OCULTO"
      />

      <StatusBlock accent={A} rows={[
        { k: 'tipo', v: 'ia seo · geo' },
        { k: 'formato', v: 'llms.txt + robots.txt + checklist' },
        { k: 'esforço', v: '30min copy + paste' },
        { k: 'valor', v: 'indexação em ChatGPT, Claude e Perplexity' },
      ]} />

      <motion.section {...fade(0.05)} style={{ marginBottom: '2.5rem' }}>
        <H2 accent={A}>1. template llms.txt</H2>
        <p style={{ color: '#aaa', fontSize: '0.85rem', lineHeight: 1.65, marginBottom: '1rem' }}>
          Salva isso em <code style={{ fontFamily: MONO, background: '#141414', padding: '0.1rem 0.35rem', borderRadius: 3, fontSize: '0.78rem', color: A.text }}>/llms.txt</code> no root do seu domínio. Preenche os <code style={{ fontFamily: MONO, background: '#141414', padding: '0.1rem 0.35rem', borderRadius: 3, fontSize: '0.78rem' }}>[colchetes]</code> e sobe. É só Markdown, nada especial.
        </p>
        <DownloadRow>
          <DownloadButton content={LLMS_TEMPLATE} filename="llms.txt" accent={A} source="llms-template" />
        </DownloadRow>
        <CodeBlock lang="markdown" accent={A} source="llms-template">{LLMS_TEMPLATE}</CodeBlock>
      </motion.section>

      <motion.section {...fade(0.08)} style={{ marginBottom: '2.5rem' }}>
        <H2 accent={A}>2. robots.txt com content-signal</H2>
        <p style={{ color: '#aaa', fontSize: '0.85rem', lineHeight: 1.65, marginBottom: '1rem' }}>
          A diretiva <code style={{ fontFamily: MONO, background: '#141414', padding: '0.1rem 0.35rem', borderRadius: 3, fontSize: '0.78rem', color: A.text }}>Content-Signal</code> é a proposta da Cloudflare adotada por crawlers que respeitam o EU AI Act. Diz pra IA: pode indexar pra responder pergunta, não pode usar pra treinar modelo.
        </p>
        <DownloadRow>
          <DownloadButton content={ROBOTS_TXT} filename="robots.txt" accent={A} source="llms-template-robots" />
        </DownloadRow>
        <CodeBlock lang="robots" accent={A} source="llms-template-robots">{ROBOTS_TXT}</CodeBlock>
      </motion.section>

      <motion.section {...fade(0.11)} style={{ marginBottom: '2.5rem' }}>
        <H2 accent={A}>3. três prompts pra testar visibilidade</H2>
        <p style={{ color: '#aaa', fontSize: '0.85rem', lineHeight: 1.65, marginBottom: '1.25rem' }}>
          Cola no ChatGPT, Claude, Perplexity e Gemini. Se você não aparece ou aparece errado, você tem trabalho a fazer.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
          {PROMPTS.map((p, i) => (
            <div key={i} style={{
              padding: '1rem 1.2rem',
              background: 'linear-gradient(180deg, #111, #0d0d0d)',
              border: '1px solid #1e1e1e',
              borderRadius: 8,
            }}>
              <p style={{ fontFamily: MONO, fontSize: '0.74rem', color: A.text, fontWeight: 600, marginBottom: '0.5rem', letterSpacing: '0.06em' }}>{p.title}</p>
              <CodeBlock lang="prompt" accent={A} source={`llms-template-prompt-${i + 1}`}>{p.prompt}</CodeBlock>
              <p style={{ fontSize: '0.78rem', color: '#aaa', lineHeight: 1.6 }}>{p.note}</p>
            </div>
          ))}
        </div>
      </motion.section>

      <motion.section {...fade(0.14)} style={{ marginBottom: '2.5rem' }}>
        <H2 accent={A}>4. checklist de implementação</H2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {CHECKLIST.map((item, i) => {
            const checked = done.has(i)
            return (
              <label key={i} style={{
                display: 'flex',
                gap: '0.75rem',
                padding: '0.75rem 1rem',
                background: checked ? A.bg : 'rgba(255,255,255,0.015)',
                border: `1px solid ${checked ? A.border : '#1e1e1e'}`,
                borderRadius: 7,
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}>
                <input
                  type="checkbox" checked={checked} onChange={() => toggle(i)}
                  style={{ accentColor: A.solid, cursor: 'pointer', marginTop: 3 }}
                />
                <span style={{ fontSize: '0.86rem', lineHeight: 1.55, color: checked ? A.text : '#e5e5e5' }}>{item}</span>
              </label>
            )
          })}
        </div>
        <p style={{ fontFamily: MONO, fontSize: '0.72rem', color: '#555', marginTop: '0.8rem' }}>
          {'// '}{done.size}/{CHECKLIST.length} concluídos
        </p>
      </motion.section>

      <ResourceContactCTA accent={A} source="llms-template" />
      <SignatureFooter accent={A} />
    </ResourceLayout>
  )
}
